import React from "react";

import styled from "styled-components/macro";

import { useGetHouseholdDetail } from "HouseholdProfile/API/queries";
import { DateTime } from "luxon";

import FnrButton3 from "UI/FnrButton3";

import { formatNumberWithCommas } from "Utilities/Number";

import DetailProgressBar from "./DetailProgressBar";
import EndPledge from "./EndPledge";

export default function PledgeDetail({
  pledgeID,
  campaignName,
  householdID,
  totalPledged,
  amountGiven,
  intervalDescription,
  amountPerInterval = 0,
  fundColor,
  status,
  startDate,
  endDate,
  userID,
  createdByUserID,
  createdByName,
  createdByPhotoURL,
  canClosePledge,
  endedOnDate,
  endedByUserID,
  endedByName,
  endedByPhotoURL,
  hasRecurring,
  fundID,
  canReceivePayments,
  onPledgeEnd,
}) {
  const startDateObj = DateTime.fromISO(startDate);
  const endDateObj = DateTime.fromISO(endDate);

  const endedOnDateObj = endedOnDate ? DateTime.fromISO(endedOnDate) : false;

  const now = DateTime.now();
  let percentOfMaxDate = false;

  const profileUserIsCurrentUser = userID === window.data.userID;

  const showMakePaymentButton =
    !hasRecurring && profileUserIsCurrentUser && canReceivePayments;

  if (status === "active" && now >= startDateObj && now <= endDateObj) {
    const totalDays = endDateObj.diff(startDateObj, ["days"]).values.days;
    const daysPast = now.diff(startDateObj, ["days"]).values.days;
    percentOfMaxDate = (daysPast / totalDays) * 100;
  }

  const loadUserProfile = (userID = null) => {
    userID = userID || createdByUserID;
    if (!userID || isNaN(userID)) return;
    window.fnReactBus?.loadUserProfile(userID);
  };

  const { data } = useGetHouseholdDetail();
  const householdHasMultipleMembers =
    data?.householdProfile?.members?.length > 1;

  return (
    <Wrapper>
      <Inside>
        <Top>
          <Title>
            {campaignName}
            <Dates startDate={startDateObj} endDate={endDateObj} />
          </Title>

          <AmountLine>
            <Amount>${formatNumberWithCommas(amountPerInterval)}</Amount>
            <Interval>{intervalDescription}</Interval>
          </AmountLine>
        </Top>

        <DetailProgressBar
          amountGiven={amountGiven}
          totalPledged={totalPledged}
          percentOfMaxDate={percentOfMaxDate}
          fundColor={fundColor}
          status={status}
        />

        <Bottom>
          <div>
            {/* {createdByUserID && createdByUserID !== userID && ( */}
            {createdByUserID && householdHasMultipleMembers && (
              <CreatedBy>
                Made by
                <UserLink
                  onClick={loadUserProfile}
                  className={
                    "can_mini_profile can_mini_profile_" + createdByUserID
                  }
                  data-userid={createdByUserID}
                >
                  <UserPhoto
                    style={{
                      backgroundImage: "url(" + createdByPhotoURL + ")",
                    }}
                  />
                  <StrongStyle>{createdByName}</StrongStyle>
                </UserLink>
              </CreatedBy>
            )}
            {endedOnDate && (
              <CreatedBy>
                Ended on{" "}
                {endedOnDateObj.toFormat("LLLL d") +
                  (endedOnDateObj.toFormat("yyyy") !== now.toFormat("yyyy")
                    ? ", " + endedOnDateObj.toFormat("yyyy")
                    : "")}
                {endedByUserID && endedByUserID !== userID && (
                  <>
                    {" "}
                    by
                    <UserLink
                      onClick={loadUserProfile}
                      className={
                        "can_mini_profile can_mini_profile_" + endedByUserID
                      }
                      data-userid={endedByUserID}
                    >
                      <UserPhoto
                        onClick={() => loadUserProfile(endedByUserID)}
                        style={{
                          backgroundImage: "url(" + endedByPhotoURL + ")",
                        }}
                      />
                      <StrongStyle
                        onClick={() => loadUserProfile(endedByUserID)}
                      >
                        {endedByName}
                      </StrongStyle>
                    </UserLink>
                  </>
                )}
              </CreatedBy>
            )}
          </div>

          <Buttons>
            {showMakePaymentButton ? (
              <MakeAPayment>
                <a
                  href={`${
                    window.location.origin
                  }/give/${fundID}?pledgeID=${pledgeID}&amount=${
                    parseInt(amountPerInterval) * 100
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <FnrButton3 size="small" color="#00B44E">
                    Make Payment
                  </FnrButton3>
                </a>
              </MakeAPayment>
            ) : null}

            {canClosePledge && (
              <EndPledge
                pledgeID={pledgeID}
                status={status}
                onPledgeEnd={onPledgeEnd}
                userID={userID}
              />
            )}
          </Buttons>
        </Bottom>
      </Inside>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  overflow: hidden;
  position: relative;
  color: var(--pledgesTextColor);
  padding: 16px 0;
  border-bottom: 1px solid var(--pledgesProgressBarBackground);

  &:first-of-type {
    padding-top: 0;
  }

  &:last-of-type {
    /* padding-bottom: 0; */
    border-bottom: none;
  }
`;
const Inside = styled.div`
  /* padding: 24px; */
`;

const AmountLine = styled.div`
  display: flex;
  align-items: stretch;
  border: 3px solid var(--textPrimary);
  border-radius: 18px;
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.3) 1px 0 3px;
  margin-bottom: -2px;
  margin-right: 8px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  flex-shrink: 0;
  flex-grow: 0;
  margin-left: auto;
`;
const Amount = styled.div`
  font-weight: 800;
  color: var(--textInverse);
  font-size: 20px;
  background: var(--textPrimary);
  padding: 0 8px;
  padding-left: 10px;
  margin-right: -3px;
  border-top-left-radius: 14px;
  border-bottom-left-radius: 0;
  position: relative;
  &::before {
    content: "";
    background: var(--textPrimary);
    height: 5px;
    width: 8px;
    transform: rotate(-45deg) translate(-1px, -2px);
    display: block;
    position: absolute;
    top: 0;
    left: 100%;
  }
  &::after {
    content: "";
    background: var(--textPrimary);
    height: 5px;
    width: 8px;
    transform: rotate(45deg) translate(-1px, 2px);
    display: none;
    position: absolute;
    bottom: 0;
    left: 100%;
  }
`;
const Interval = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 600;
  padding: 0 10px;
  border: 3px solid var(--textPrimary);
  display: flex;
  align-items: center;
  border-radius: 18px;
  padding-top: 1px;
  border-bottom-right-radius: 9px;
  border-bottom-left-radius: 9px;
  margin-top: -3px;
  margin-bottom: -3px;
  margin-right: -3px;
`;

const DefaultPadding = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Top = styled(DefaultPadding)`
  padding: 0 16px;
  position: relative;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const Title = styled.div`
  font-size: 22px;
  padding-bottom: 4px;
  margin-right: 20px;
`;

const MakeAPayment = styled.div`
  grid-area: payment;
  text-align: right;
`;

const Bottom = styled(DefaultPadding)`
  padding: 10px 16px 0 16px;
  font-size: 14px;
`;

const StrongStyle = styled.span`
  font-weight: 400;
`;

const CreatedBy = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 560px) {
    // things get too crowded on small screens if buttons are showing, so hiding this.
    display: none;
  }
`;
const UserPhoto = styled.div`
  width: 24px;
  height: 24px;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-color: #fff;
  box-sizing: border-box;
  border-radius: 32px;
  border: 1px solid #fff;
  box-shadow: rgba(0, 0, 0, 0.4) 1px 1px 3px;
  margin: 0 6px;
`;
const UserLink = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const Buttons = styled.div`
  display: flex;
  gap: 6px;
`;

const Dates = ({ startDate, endDate }) => {
  return (
    <DateWrapper>
      <StrongStyle>
        {startDate.toFormat("LLL d")}
        {startDate.toFormat("yyyy") !== endDate.toFormat("yyyy") &&
          ", " + startDate.toFormat("yyyy")}
      </StrongStyle>
      {" to "}
      <StrongStyle>
        {(startDate.toFormat("LLL") !== endDate.toFormat("LLL") ||
          startDate.toFormat("yyyy") !== endDate.toFormat("yyyy")) &&
          endDate.toFormat("LLL ")}
        {endDate.toFormat("d, yyyy")}
      </StrongStyle>
    </DateWrapper>
  );
};
const DateWrapper = styled.div`
  font-size: 13px;
  margin-top: -2px;
  padding-bottom: 4px;
`;
